<script setup>
import * as permissions from '@/common/permissionConstants';
import BnAvatar from '@/components/BnAvatar.vue';
import BnButton from '@/components/BnButton.vue';
import BnContactSearch from '@/components/BnContactSearch.vue';
import BnDialog from '@/components/BnDialog.vue';
import BnMenu from '@/components/BnMenu.vue';
import BnMenuItem from '@/components/BnMenuItem.vue';
import BnMyAccount from '@/components/account/BnMyAccount.vue';
import BnContactEditDialog from '@/components/contacts/BnContactEditDialog.vue';
import { IonContent, IonPage } from '@ionic/vue';
import { computed, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import { addRefToBackBtnListiner, goBack, mobileBackNav, removeNativePreviousNavigations, removeRefToBackBtnListiner } from '@/common/nativeBackButton';
import BnFooter from '@/components/BnFooter';
import { useDevOnly } from '@/composables/useDevOnly';
import { useUsersnap } from '@/composables/useUsersnap';
import { useVersionCheck } from '@/composables/useVersionCheck';
import router from '@/router';
import { useAuthStore } from '@/stores/auth';
import { Capacitor } from '@capacitor/core';
import { onClickOutside } from '@vueuse/core';
import startCase from 'lodash/startCase';

import { addDays, format, parseISO } from 'date-fns';
import { useDisplay } from 'vuetify';
const vuetifyDisplay = useDisplay();

const app = getCurrentInstance();
const isPortal = app.appContext.config.globalProperties?.isPortal;

import { useRoute } from 'vue-router';
const route = useRoute();
const authStore = useAuthStore();
const { isBnSupport, devOnly } = useDevOnly();
const { updateAvailable, reloadForUpdate } = useVersionCheck();

const platform_ios = ref(true);
const drawer = ref(true);
const searchMenu = ref(false);

// template refs
const contactMobileSearch = ref(null);
const contactSearch = ref(null);
const bnDialogRef = ref(null);
const myAccountDialog = ref(null);
const navDrawerMain = ref(null);
const contactEditDialog = ref(null);
const ionHeaderRef = ref(null);

import { useStateFlow } from '@/common/workflowEngine/useStateFlow';
import { useNavBar } from '@/composables/useNavBar';
import { usePortalApplication } from '@/composables/usePortalApplication';
const { keepDrawerOpen, secondNavWidth } = useNavBar();
const { startNewApplication } = usePortalApplication();

const mainNavWidth = 80;
const topBarHeight = ref(58);
const isNativePlatform = Capacitor.isNativePlatform();

const navItemsEhr = [
    {
        title: 'Home',
        icon: ['far', 'columns'],
        route: { name: 'dashboard' },
        activeRoutes: ['dashboard'],
    },
    {
        title: 'Contacts',
        icon: ['far', 'users'],
        route: { name: 'contacts' },
        activeRoutes: ['contacts', 'patient', 'practitioner', 'organization'],
    },
    {
        title: 'Calendar',
        icon: ['far', 'calendar-alt'],
        route: { name: 'calendar' },
        activeRoutes: ['calendar'],
        keepOpen: true,
    },
    {
        title: 'General log',
        icon: ['far', 'cabinet-filing'],
        route: { name: 'general-log' },
        activeRoutes: ['general-log'],
        keepOpen: true,
        requiredPermission: permissions.VIEW_GENERAL_LOG,
    },
    {
        title: 'Reporting',
        icon: ['far', 'analytics'],
        route: { name: 'reports' },
        activeRoutes: ['reports'],
        keepOpen: true,
    },
    {
        title: 'Lens',
        icon: ['far', 'wand-magic-sparkles'],
        route: { name: 'lens' },
        activeRoutes: ['lens', 'lens-builder', 'lens-view'],
        keepOpen: true,
        bnSupportOnly: true,
        requiredPermission: permissions.LENS_VIEW,
    },
    {
        title: 'Financial',
        icon: ['far', 'file-invoice-dollar'],
        route: { name: 'financial-page' },
        activeRoutes: ['financial-page'],
        keepOpen: false,
        requiredPermission: permissions.VIEW_CLAIMS,
    },
    {
        title: 'Settings',
        icon: ['far', 'cog'],
        route: { name: 'settings' },
        activeRoutes: ['settings'],
        keepOpen: true,
    },
    {
        title: 'Messages',
        icon: ['far', 'comment-alt'],
        route: { name: 'messages' },
        activeRoutes: ['messages'],
        devOnly: true,
    },
];

const navItemsPortal = [
    {
        title: 'Home',
        icon: ['fas', 'columns'],
        route: { name: 'dashboard' },
    },
    {
        title: 'Files',
        icon: ['far', 'hdd'],
        route: { name: 'files' },
    },
    {
        title: 'Resources',
        icon: ['far', 'link'],
        route: { name: 'resources' },
    },
];

const navDrawerContent = ref(null);
const twoNavs = ref(false);

let observer;

const setActiveAgency = async () => {
    let timeout = setTimeout(() => {
        setActiveBrand();
        clearTimeout(timeout);
        timeout = null;
    }, 250);
};

const updateHeaderHeight = () => {
    if (ionHeaderRef.value) {
        nextTick(() => {
            // Get the actual header element
            const headerEl = ionHeaderRef.value.$el;
            const headerHeight = headerEl.offsetHeight;

            // For iPad, we need to account for the safe area
            if (platform_ios.value && Capacitor.isNativePlatform() && (window.innerWidth >= 768 || window.innerHeight >= 768)) {
                // iPad detection
                // Get the computed style to check if there's a safe area inset
                const computedStyle = window.getComputedStyle(headerEl);
                const paddingTop = parseInt(computedStyle.paddingTop, 10) || 0;

                // Use either the calculated height or add the safe area padding if detected
                topBarHeight.value = headerHeight > 0 ? headerHeight : topBarHeight.value + paddingTop;

                // We can also check for the actual safe area on iOS
                if (window.webkit?.messageHandlers?.statusBarHeight) {
                    // Add listener for safe area changes (device rotation etc)
                    window.addEventListener('resize', updateHeaderHeight);
                }
            } else if (headerHeight > 0) {
                // For non-iPad devices, just use the measured height
                topBarHeight.value = headerHeight;
            }
        });
    }
};

// Add an event listener for iOS device orientation changes
const setupOrientationListener = () => {
    if (platform_ios.value && Capacitor.isNativePlatform()) {
        window.addEventListener('orientationchange', () => {
            // Delay update to allow iOS to adjust safe areas
            setTimeout(updateHeaderHeight, 300);
        });
    }
};

const hasPerspectivesExtension = ref(true); // default to true so the button is not shown by default
const hasPerspectivesAccess = computed(() => {
    return authStore.hasPermission(permissions.PERSPECTIVES_ACCESS);
});

const hasSupaNotesExtension = ref(true); // default to true so the button is not shown by default
const hasSupaNotesAccess = computed(() => {
    return authStore.hasPermission(permissions.SUPANOTES_ACCESS);
});

const accountReadOnlyUntil = computed(() => {
    if (!authStore.account?.canceledDate) return null;
    const canceledDate = parseISO(authStore.account.canceledDate);
    return format(addDays(canceledDate, 90), 'MMM d, yyyy');
});

const installPerspectives = () => {
    if (hasPerspectivesAccess.value) {
        window.open('https://www.perspectiveshealth.ai/bestnotes-guide', '_blank');
    }
};

const installSupaNotes = () => {
    if (hasSupaNotesAccess.value) {
        window.open('https://chromewebstore.google.com/detail/supanote/cocmdandgaakjfmcljfpakjclfbfpnpo', '_blank');
    }
};

const checkPerspectivesExtension = () => {
    if (hasPerspectivesAccess.value) {
        // Check if the extension has set a marker on the window object
        if (window.PERSPECTIVES_INSTALLED) {
            hasPerspectivesExtension.value = true;
        } else {
            hasPerspectivesExtension.value = false;
        }
    }
};

const checkSupaNotesExtension = () => {
    if (hasSupaNotesAccess.value) {
        if (window.SUPANOTES_INSTALLED) {
            hasSupaNotesExtension.value = true;
        } else {
            hasSupaNotesExtension.value = false;
        }
    }
};

onMounted(() => {
    const { initializeUsersnap } = useUsersnap();
    // this.savingGetter = route.meta.savingGetter || 'isSaving';

    if (vuetifyDisplay.smAndDown.value) {
        drawer.value = false;
    }

    if (!vuetifyDisplay.xs.value && !window.Cypress) {
        initializeUsersnap();
    }

    window.addEventListener('defaultLayout.setActiveAgency', setActiveAgency);
    window.addEventListener('resize', updateHeaderHeight);

    observer = new MutationObserver(() => {
        twoNavs.value = !!navDrawerContent.value?.children?.length;
    });

    observer.observe(navDrawerContent.value, { childList: true, subtree: true });

    // Measure header height after mount
    setupOrientationListener();
    updateHeaderHeight();

    // Load related persons for portal users
    if (isPortal) {
        loadRelatedPersons();
    }

    checkPerspectivesExtension();
    checkSupaNotesExtension();
});

onBeforeUnmount(() => {
    window.removeEventListener('defaultLayout.setActiveAgency', setActiveAgency);
    window.removeEventListener('resize', updateHeaderHeight);
    window.removeEventListener('orientationchange', updateHeaderHeight);
    observer?.disconnect();
});

// when clicking outside mobile search bar close search bar,  Need to set timeout for code to execute when opening a patient
onClickOutside(contactMobileSearch, () => {
    let timeout = setTimeout(() => {
        searchMenu.value = false;
        clearTimeout(timeout);
        timeout = null;
    }, 250);
});

const brandName = computed(() => {
    return authStore.brand?.name || authStore.organizationAccount?.name;
});

const location = computed(() => {
    return authStore.brand?.location;
});

const brandLogo = computed(() => {
    return authStore.brand?.logo || authStore.account.logo;
});

const userBrands = computed(() => {
    return authStore.userAgencies;
});

const multipleBrandsLocations = computed(() => {
    return userBrands.value?.length > 1 || userBrands.value.some((item) => item.locations?.length > 1);
});

// Load related persons from store
const loadRelatedPersons = async () => {
    if (!isPortal) return;
    await authStore.loadRelatedPersons();
};

const viewingSelfDisplay = computed(() => {
    if (!authStore.portalViewingPatient) return 'Myself';
    return authStore.portalViewingPatient?.id === authStore.patient?.id ? 'Myself' : authStore.portalViewingPatient.firstName || 'Unknown';
});

const routes = computed(() => {
    let endRoutes = [];
    let computedRoutes = [];
    let routes = route.matched.filter((r) => r.name || r.meta.label || r.meta.path);

    routes.forEach((r) => {
        if (r.meta.path) {
            r.meta.path.forEach((val) => endRoutes.push(router.resolve({ name: val }).route));
        }

        endRoutes.push(r);
    });

    endRoutes.forEach((route) => {
        computedRoutes.push({
            text: getTitle(route),
            href: route.path,
            disabled: false,
            rawRoute: route,
        });
    });

    // disable last entry
    computedRoutes[computedRoutes.length - 1].disabled = true;

    return computedRoutes;
});

const isSaving = computed(() => {
    return false;
    // return this.$store.getters[this.savingGetter];
});

const navItems = computed(() => {
    if (isPortal) {
        return navItemsPortal;
    } else {
        return navItemsEhr.filter((item) => {
            if (item.requiredPermission) return authStore.hasPermission(item.requiredPermission);
            return true;
        });
    }
});

const browserTitle = computed(() => {
    let routeArray = Array.from(routes).reverse();
    let titleAddArray = routeArray.filter((i) => i.rawRoute.meta.includeInTitle).map((i) => i.rawRoute.meta.label || startCase(i.rawRoute.name));
    titleAddArray.unshift(getTitle());
    let newTitle = titleAddArray.length ? titleAddArray.join(' · ') : '';
    document.title = newTitle + ' | BestNotes';
    return newTitle;
});

const isNavItemActive = (item) => {
    if (!item.activeRoutes) return false;

    // Check if any matched route names are in the activeRoutes array
    return route.matched.some((matchedRoute) => {
        return item.activeRoutes.includes(matchedRoute.name);
    });
};

const avatarResource = computed(() => {
    return {
        resourceType: 'Organization',
        logo: brandLogo.value,
        name: brandName.value,
    };
});

/**
 * Watchers
 */
watch(
    () => vuetifyDisplay.smAndDown,
    (newVal) => {
        drawer.value = !newVal;
    },
);

watch(drawer, () => {
    if (keepDrawerOpen.value) {
        drawer.value = true;
        keepDrawerOpen.value = false;
        navDrawerMain.value.isActive = true;
    }
});

/**
 * Methods
 */

const { showUsersnap } = useUsersnap();

const signOut = async () => {
    const options = {
        header: 'Sign out',
        message: 'Are you sure you want to sign out?',
        confirm: 'Sign out',
        cancel: 'Cancel',
        width: 400,
    };
    bnDialogRef.value.show(options).then(async (confirm) => {
        if (!confirm) return;

        try {
            if (!isPortal) {
                const { resetApplicationState } = useStateFlow();
                await resetApplicationState({ practitioner: authStore.practitioner });
            }
        } catch (error) {
            console.warn('Error during sign out process:', error);
        } finally {
            // Always navigate to logout, regardless of success or failure
            router.push({ name: 'logout' });
        }
    });
};

const setActiveBrand = (brandLocation) => {
    authStore.setBrand(brandLocation);
};
const addContact = (item) => {
    const options = {
        header: 'Add contact',
        persistent: true,
    };
    const props = {
        add: item,
    };
    addRefToBackBtnListiner(contactEditDialog, 'close', 'contactEditDialog');
    contactEditDialog.value.show(options, props).then(async (resource) => {
        removeRefToBackBtnListiner('contactEditDialog');
        if (resource) {
            router.push({ name: resource.resourceType.toLowerCase(), params: { id: resource.id, activeTab: 'contact' } });
        }
    });
    contactSearch.value.search = '';
    searchMenu.value = false;
};

const openContact = (item) => {
    searchMenu.value = false;
    router.push({ name: item.resourceType.toLowerCase(), params: { id: item.id } });
};

const getTitle = (r) => {
    if (!r) {
        r = route;
    }

    return r.meta.label || startCase(r.name);
};

const routeToPage = (item) => {
    // only navigate if the route has actually changed (avoid duplicate navigation error)
    if (item.route.name !== route.name) {
        router.push(item.route);

        if (!item.keepOpen && vuetifyDisplay.smAndDown.value === true) {
            drawer.value = false;
        }
    }
    removeNativePreviousNavigations();
};
const mobileBackNavForRelatedContacts = () => {
    goBack();
};
const filterOutPayers = (e) => {
    if (e.resourceType === 'Organization') {
        return !e?.type?.some((f) => f.coding.some((i) => i.code === 'pay'));
    }
    return true;
};

const setDrawer = () => {
    drawer.value = !drawer.value;
};

const closeDrawer = () => {
    if (vuetifyDisplay.smAndDown.value) {
        drawer.value = false;
    }
};
</script>

<template>
    <IonPage>
        <div style="display: contents">
            <v-navigation-drawer
                ref="navDrawerMain"
                v-model="drawer"
                order="2"
                :absolute="!vuetifyDisplay.smAndDown.value"
                theme="dark"
                :permanent="!vuetifyDisplay.smAndDown.value"
                :temporary="vuetifyDisplay.smAndDown.value"
                rail
                :rail-width="mainNavWidth"
                class="pt-1"
                :class="{ openMainDrawer: drawer }"
                :style="`z-index: 1100; overflow-x: hidden; top: ${topBarHeight}px;`"
                disable-route-watcher
            >
                <v-list two-line class="text-center">
                    <v-list-item
                        v-for="item in navItems.filter((e) => (e.devOnly ? devOnly() : true) && (e.bnSupportOnly ? isBnSupport() : true))"
                        :key="item.title"
                        :active="isNavItemActive(item)"
                        class="px-0 justify-center"
                        :class="{ 'bn-dev-only': item.devOnly }"
                        active-class="border-left"
                        color="accent"
                        @click="routeToPage(item)"
                    >
                        <font-awesome-icon :icon="item.icon" size="lg" style="color: white" />
                        <v-list-item-title class="text-caption text-white" style="font-size: 12px">{{ item.title }}</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-navigation-drawer>

            <v-navigation-drawer
                ref="navDrawer"
                v-model="drawer"
                order="2"
                :permanent="!vuetifyDisplay.smAndDown.value"
                :temporary="vuetifyDisplay.smAndDown.value"
                :scrim="false"
                :absolute="!vuetifyDisplay.smAndDown.value"
                disable-route-watcher
                :width="twoNavs ? secondNavWidth : 0"
                :style="`z-index: 1100; overflow-x: hidden; top: ${topBarHeight}px;`"
                :class="{ ios_header_padding: platform_ios, openSecondaryDrawer: drawer, 'd-none': !twoNavs }"
            >
                <v-row class="flex-nowrap" no-gutters>
                    <v-list class="d-flex flex-column scroll-on">
                        <div id="navDrawer" ref="navDrawerContent" @click="closeDrawer"></div>
                    </v-list>
                </v-row>
            </v-navigation-drawer>

            <ion-header ref="ionHeaderRef" class="ion-no-border">
                <ion-toolbar>
                    <v-app-bar order="1" density="comfortable" flat color="#f5f5f5" short style="z-index: 110">
                        <!-- Small devices - display navigation hamburger -->
                        <div :class="isNativePlatform ? 'ml-1' : 'ml-3'">
                            <BnButton v-if="vuetifyDisplay.smAndDown.value" icon="bars" @click="setDrawer" />
                            <!-- Large devices - always display search -->
                            <div v-if="!isPortal && !vuetifyDisplay.smAndDown.value" style="width: 300px" class="mr-2 flex-grow-1">
                                <BnContactSearch
                                    ref="contactSearch"
                                    variant="outlined"
                                    flat
                                    default-query="recent"
                                    density="compact"
                                    label=""
                                    placeholder="Search/add contacts"
                                    :filter="filterOutPayers"
                                    data-cy="primaryContactSearch"
                                    @add-new-clicked="addContact"
                                    @item-clicked="openContact"
                                />
                            </div>
                        </div>
                        <!--  Mobile Back Navigation for Related Contacts -->
                        <div v-if="isNativePlatform && mobileBackNav.status" class="d-flex align-center text-truncate pl-1 pr-3">
                            <BnButton icon="arrow-left" size="lg" :style="{ color: '#000 !important' }" @click="mobileBackNavForRelatedContacts" />
                        </div>
                        <!-- All devices - Show active brand with selector if required -->
                        <div class="d-flex align-center text-truncate">
                            <BnAvatar v-if="vuetifyDisplay.smAndUp.value" :resource="avatarResource" no-badge is-public size="36"></BnAvatar>
                            <div class="text-truncate ml-2" data-cy="brandName">
                                {{ brandName }}
                                <div v-if="location" class="text-caption mt-n1">
                                    <font-awesome-icon :icon="['far', 'map-marker-alt']" />
                                    {{ location.display || getDisplay(location) }}
                                </div>
                            </div>
                            <template v-if="multipleBrandsLocations">
                                <BnMenu offset-y left :icon="['fas', 'caret-down']" icon-size="lg" class="ml-2">
                                    <template v-for="brand in userBrands" :key="brand.id">
                                        <BnMenuItem class="py-0" disabled :label="brand.name || getDisplay(brand)" icon="building" />
                                        <template v-for="brandLocation in brand.locations" :key="brandLocation.id">
                                            <BnMenuItem
                                                class="pl-6 py-0"
                                                icon="map-marker-alt"
                                                :label="brandLocation.display || getDisplay(brandLocation)"
                                                @click="setActiveBrand({ id: brand.id, name: brand.name, location: brandLocation })"
                                            />
                                        </template>
                                    </template>
                                </BnMenu>
                            </template>
                        </div>
                        <!-- Viewing person selector for portal -->
                        <v-menu v-if="isPortal && authStore.portalViewingPatient" offset-y>
                            <template #activator="{ props }">
                                <v-btn v-bind="props" variant="text" class="text-capitalize">
                                    <span class="text-caption text-grey mr-2">Viewing:</span>
                                    <BnAvatar :resource="authStore.portalViewingPatient" no-badge is-public size="32" class="mr-2" />
                                    <span>{{ viewingSelfDisplay }}</span>
                                    <font-awesome-icon :icon="['fas', 'caret-down']" class="ml-2" />
                                </v-btn>
                            </template>
                            <v-list>
                                <v-list-item v-for="person in authStore.portalPatientItems" :key="person.patient?.id" @click="authStore.setPortalViewingPatientId(person.patient.id)">
                                    <template #prepend>
                                        <BnAvatar :resource="person.patient" no-badge is-public size="32" class="mr-3" />
                                    </template>
                                    <v-list-item-title>{{ person.display }}</v-list-item-title>
                                </v-list-item>
                                <v-divider class="my-2" />
                                <v-list-item @click="startNewApplication">
                                    <template #prepend>
                                        <font-awesome-icon :icon="['far', 'plus']" size="lg" class="mr-3 ml-1" />
                                    </template>
                                    <v-list-item-title>New application for someone else</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                        <v-spacer></v-spacer>

                        <!-- Small devices - move search to button icon -->
                        <template v-if="!isPortal && vuetifyDisplay.smAndDown.value">
                            <v-menu v-model="searchMenu" :close-on-content-click="false" offset-y min-width="320">
                                <template #activator="{ props }">
                                    <v-btn v-bind="props" icon>
                                        <font-awesome-icon :icon="['far', 'search']" size="lg" />
                                    </v-btn>
                                </template>
                                <v-card ref="contactMobileSearch" min-width="300" class="mx-auto fill-width" variant="outlined">
                                    <BnContactSearch
                                        ref="contactSearch"
                                        autofocus
                                        default-query="recent"
                                        density="compact"
                                        variant="outlined"
                                        :filter="filterOutPayers"
                                        label=""
                                        placeholder="Search contacts"
                                        data-cy="primaryContactSearch"
                                        @add-new-clicked="addContact"
                                        @item-clicked="openContact"
                                    />
                                </v-card>
                            </v-menu>
                        </template>
                        <v-btn v-if="updateAvailable" color="primary" variant="outlined" class="ml-2 text-capitalize" @click="reloadForUpdate">
                            <font-awesome-icon :icon="['far', 'arrow-rotate-right']" />
                            <span class="ml-2">Update available - Refresh</span>
                        </v-btn>
                        <v-tooltip v-else-if="authStore.account?.suspendedDate" location="bottom">
                            <template #activator="{ props: tooltipProps }">
                                <v-chip v-bind="tooltipProps" color="warning" variant="outlined" size="large" class="ml-2">
                                    <font-awesome-icon :icon="['far', 'pause-circle']" />
                                    <span class="ml-2">Account suspended</span>
                                    <font-awesome-icon :icon="['far', 'circle-info']" class="ml-2" />
                                </v-chip>
                            </template>
                            This account has been suspended. You have read-only access until the suspension is removed. Contact support for assistance.
                        </v-tooltip>
                        <v-tooltip v-else-if="authStore.account?.canceledDate" location="bottom">
                            <template #activator="{ props: tooltipProps }">
                                <v-chip v-bind="tooltipProps" color="error" variant="outlined" size="large" class="ml-2">
                                    <font-awesome-icon :icon="['far', 'ban']" />
                                    <span class="ml-2">Account canceled</span>
                                    <font-awesome-icon :icon="['far', 'circle-info']" class="ml-2" />
                                </v-chip>
                            </template>
                            Account has been canceled. You will have read-only access until {{ accountReadOnlyUntil }}.
                        </v-tooltip>
                        <v-btn v-else-if="!hasPerspectivesExtension" color="primary" variant="outlined" class="ml-2 text-capitalize" @click="installPerspectives">
                            <font-awesome-icon :icon="['far', 'puzzle-piece']" />
                            <span class="ml-2">Install Perspectives AI</span>
                        </v-btn>
                        <v-btn v-else-if="!hasSupaNotesExtension" color="primary" variant="outlined" class="ml-2 text-capitalize" @click="installSupaNotes">
                            <font-awesome-icon :icon="['far', 'puzzle-piece']" />
                            <span class="ml-2">Install SUPANotes</span>
                        </v-btn>
                        <v-menu>
                            <template #activator="{ props }">
                                <v-btn v-bind="props" rounded large class="ml-2 text-capitalize" text :icon="vuetifyDisplay.smAndDown.value" data-cy="userMenu">
                                    <span v-if="!vuetifyDisplay.smAndDown.value" class="mr-2"> Hi, {{ authStore.firstName }} </span>
                                    <BnAvatar :resource="authStore.aidboxPersonResource" no-badge is-public size="36" />
                                </v-btn>
                            </template>

                            <v-card class="mx-auto" outlined>
                                <picture>
                                    <source srcset="../../assets/img/docks_344x200.webp" type="image/webp" />
                                    <img src="../../assets/img/docks_344x200.jpg" alt="Docks" style="width: 344px; height: 200px" />
                                </picture>
                                <v-card-title class="mt-n16" style="color: white">{{ authStore.fullName || 'Unnamed User' }}</v-card-title>

                                <v-spacer></v-spacer>

                                <v-list>
                                    <v-list-item @click="$refs.myAccountDialog.show()">
                                        <template #prepend>
                                            <font-awesome-icon class="mr-2" :icon="['far', 'user-cog']" />
                                        </template>
                                        <v-list-item-title>My BestNotes account</v-list-item-title>
                                    </v-list-item>

                                    <v-list-item v-if="authStore.showSwitchAccount" @click="router.push({ name: 'account-selection' })">
                                        <template #prepend>
                                            <font-awesome-icon class="mr-2" :icon="['far', 'random']" />
                                        </template>
                                        <v-list-item-title>Switch account</v-list-item-title>
                                    </v-list-item>

                                    <v-list-item @click="showUsersnap">
                                        <template #prepend>
                                            <font-awesome-icon class="mr-2" :icon="['far', 'bullhorn']" />
                                        </template>
                                        <v-list-item-title>Feedback</v-list-item-title>
                                    </v-list-item>

                                    <v-list-item data-cy="lockApplication" @click="router.push({ name: 'lockout' })">
                                        <template #prepend>
                                            <font-awesome-icon class="mr-2" :icon="['far', 'user-lock']" />
                                        </template>
                                        <v-list-item-title>Lock application</v-list-item-title>
                                    </v-list-item>
                                </v-list>

                                <v-card-actions class="p-2 d-flex flex-row-reverse">
                                    <v-btn color="primary" variant="outlined" @click="signOut">Sign out</v-btn>
                                </v-card-actions>
                            </v-card>
                        </v-menu>

                        <div style="position: absolute; top: 74px; right: 16px">
                            <v-chip v-if="false" color="error">
                                <font-awesome-icon :icon="['fal', 'exclamation-triangle']"></font-awesome-icon>
                                <span class="ml-2">Retry has timed out after 5 minutes.</span>
                                <v-btn ref="retrySaves" text color="white" class="ml-1" @click="retrySaves">Retry</v-btn>
                            </v-chip>

                            <!--                        <v-chip v-else-if="hasNetworkIssues" color="error">-->
                            <!--                            <font-awesome-icon :icon="['fal', 'exclamation-triangle']"></font-awesome-icon>-->
                            <!--                            <span class="ml-2">Network Connectivity Issues, Retrying...</span>-->
                            <!--                        </v-chip>-->

                            <v-chip v-else-if="isSaving">
                                <font-awesome-icon :icon="['far', 'upload']"></font-awesome-icon>
                                <span class="ml-2">Saving...</span>
                            </v-chip>

                            <!--                        <v-chip v-else>-->
                            <!--                            <font-awesome-icon :icon="['far', 'save']"></font-awesome-icon>-->
                            <!--                            <span class="ml-2">Changes Saved</span>-->
                            <!--                        </v-chip>-->
                        </div>
                    </v-app-bar>
                </ion-toolbar>
            </ion-header>

            <IonContent>
                <v-main class="fill-height pt-0">
                    <v-container fluid class="fill-height align-baseline" :class="{ 'pa-0': vuetifyDisplay.smAndDown.value }">
                        <div style="width: inherit; height: inherit">
                            <router-view v-slot="{ Component }">
                                <transition name="fade" mode="out-in">
                                    <component :is="Component" />
                                </transition>
                            </router-view>
                        </div>
                        <!--Lower Notch Padding Protection-->
                        <br v-if="vuetifyDisplay.smAndDown.value" style="clear: both" />
                    </v-container>
                </v-main>
            </IonContent>

            <BnContactEditDialog v-if="!isPortal" ref="contactEditDialog" :auth-store="authStore" />

            <BnDialog ref="bnDialogRef" />

            <BnMyAccount ref="myAccountDialog" :is-portal="isPortal" :full-name="authStore.fullName" />
            <div class="d-none">{{ browserTitle }}</div>

            <!-- ios ample touch footer bar -->
            <BnFooter></BnFooter>
        </div>
    </IonPage>
</template>

<style lang="scss">
@use '@/assets/scss/bn.scss';

.ml-80 {
    margin-left: 80px;
}

.openMainDrawer {
    left: 0 !important;
}

.openSecondaryDrawer {
    left: 80px !important;
}

.border-left {
    border-left: 2px solid #5867dd;
}

/* Hacky fixes to get Ionic and Vuetify to work together */
ion-header {
    z-index: 110;
}

.v-app-bar {
    position: relative !important;
}
/* End Hacky Fixes */
</style>
